import { rgba } from 'polished';
import { useTranslation } from 'react-i18next';
import { Fragment } from 'react/jsx-runtime';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ChevronRightIcon, MicrosoftOutlookIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Callout,
	Card,
	Container,
	FlexGap,
	Heading,
	Heading3,
	Paragraph,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import { Header } from '../ProductLayout';
import ProductTile from '../ProductTile';

const IntegrationCard = styled(Box)<{ connected?: boolean }>`
	border: 1px solid
		${(props) => (props.connected ? tokens.border.subtle : props.theme.colors.yellow)};
	box-shadow: 0 0 0 2px
		${(props) => rgba(props.connected ? tokens.border.subtle : props.theme.colors.yellow, 0.25)};
	border-radius: 5px;
`;

const ReconsentBanner = styled(Box)`
	border: 1px solid ${(props) => props.theme.colors.yellow};
	background: ${(props) => rgba(props.theme.colors.yellow, 0.1)};
	border-radius: 5px;
`;

function InstallPath(props: { path: string[] }) {
	return (
		<FlexGap alignItems="center" gap={1}>
			{props.path.map((step, index) => (
				<Fragment key={step}>
					<Box>{step}</Box>
					{index < props.path.length - 1 && <ChevronRightIcon />}
				</Fragment>
			))}
		</FlexGap>
	);
}

function PrerequisitesSection() {
	const { t } = useTranslation();
	return (
		<Callout color="warning" mb={3} flexDirection="column">
			<Heading3>{t('settings.connectors.microsoft.prerequisites.title')}</Heading3>
			<Paragraph mb={2}>{t('settings.connectors.microsoft.prerequisites.description')}</Paragraph>
			<Flex alignItems="center" mt={3}>
				<Box flexGrow={1} flexShrink={1} pr={3}>
					<Heading3>{t('settings.connectors.microsoft.prerequisites.teams.title')}</Heading3>
					<Paragraph>
						{t('settings.connectors.microsoft.prerequisites.teams.description')}
					</Paragraph>

					<InstallPath
						path={[
							'https://admin.teams.microsoft.com',
							'Teams apps',
							'Manage Apps',
							'actions',
							'Upload new app',
						]}
					/>
				</Box>
				<Button
					type="secondary"
					title={t('settings.connectors.microsoft.prerequisites.download.cta')}
					link="https://assets.ontreams.com/downloads/teams/treams-microsoft-teams-LATEST.zip"
					target="_blank"
				/>
			</Flex>
			<Flex alignItems="center" mt={3}>
				<Box flexGrow={1} flexShrink={1} pr={3}>
					<Heading3>{t('settings.connectors.microsoft.prerequisites.outlook.title')}</Heading3>
					<Paragraph>
						{t('settings.connectors.microsoft.prerequisites.outlook.description')}
					</Paragraph>
				</Box>
				<Button
					type="secondary"
					icon={<MicrosoftOutlookIcon />}
					title={t('settings.connectors.microsoft.prerequisites.download.cta')}
					link="https://marketplace.microsoft.com/product/wa200012244"
					target="_blank"
				/>
			</Flex>
		</Callout>
	);
}

const StatusBadge = styled(Box)<{ connected?: boolean }>`
	flex-grow: 0;
	flex-shrink: 0;
	border-radius: 4px;
	background: ${(props) =>
		rgba(props.connected ? props.theme.colors.green : props.theme.colors.yellow, 0.25)};
	color: ${(props) => (props.connected ? props.theme.colors.greenDark : props.theme.colors.yellow)};
`;

function CopyConsentLinkButton(props: { onCopy: () => Promise<string | null | undefined> }) {
	const { t } = useTranslation();
	const { addToast } = useToasts();

	return (
		<Button
			type="secondary"
			title={t('settings.connectors.microsoft.consentLink.copy')}
			onClick={async () => {
				const url = await props.onCopy();
				if (!url) {
					return;
				}

				await navigator.clipboard.writeText(url);
				addToast(t('settings.connectors.microsoft.consentLink.copied'), {
					appearance: 'success',
					autoDismiss: true,
				});
			}}
		/>
	);
}

function IntegrationSection(props: {
	title: string;
	description: string;
	isConnected: boolean;
	requiresReconsent?: boolean;
	tenantId?: string | null;
	onConnect: () => void;
	onCopyLink: () => Promise<string | null | undefined>;
	onDisconnect: () => void;
}) {
	const { t } = useTranslation();

	return (
		<IntegrationCard connected={props.isConnected} mb={3} p={3}>
			<Flex alignItems="flex-start">
				<Box flexGrow={1} flexShrink={1}>
					<Heading3>{props.title}</Heading3>
					<Paragraph>{props.description}</Paragraph>
				</Box>
				<StatusBadge connected={props.isConnected} px={3} py={2} ml={2}>
					{props.isConnected ? 'Connected' : 'Not connected'}
				</StatusBadge>
			</Flex>
			{props.requiresReconsent && (
				<ReconsentBanner mt={2} p={3}>
					<Paragraph mb={2}>
						Updated permissions are required for {props.title}. An admin needs to re-approve to keep
						this integration working.
					</Paragraph>
					<FlexGap alignItems="center" gap={8}>
						<Button type="primary" title="Re-approve permissions" onClick={props.onConnect} />
						<CopyConsentLinkButton onCopy={props.onCopyLink} />
					</FlexGap>
				</ReconsentBanner>
			)}
			<Box mt={2}>
				{props.isConnected ? (
					<Box>
						{props.tenantId && <Paragraph mb={2}>Tenant ID: {props.tenantId}</Paragraph>}
						<Paragraph
							as="a"
							style={{ textDecoration: 'underline', cursor: 'pointer' }}
							onClick={props.onDisconnect}
						>
							{t('settings.connectors.microsoft.disconnect', { title: props.title })}
						</Paragraph>
					</Box>
				) : (
					<Box>
						<FlexGap alignItems="center" gap={8}>
							<Button
								type="primary"
								title={t('settings.connectors.microsoft.connect', { title: props.title })}
								onClick={props.onConnect}
							/>
							<CopyConsentLinkButton onCopy={props.onCopyLink} />
						</FlexGap>
					</Box>
				)}
			</Box>
		</IntegrationCard>
	);
}

export default function Microsoft() {
	const { data, loading, refetch } = graphql.useMicrosoftConfigQuery();
	const [connect] = graphql.useMicrosoftConnectMutation();
	const [disconnect] = graphql.useMicrosoftDisconnectMutation();

	const { t } = useTranslation();

	const config = data?.viewer.workspace.microsoftConfig;

	const requestConsentUrl = async (integration: graphql.MicrosoftIntegration) => {
		const result = await connect({ variables: { integration } });
		return result.data?.microsoftConnect;
	};

	const handleConnect = async (integration: graphql.MicrosoftIntegration) => {
		const url = await requestConsentUrl(integration);
		if (url) {
			window.location.href = url;
		}
	};

	const handleDisconnect = async (integration: graphql.MicrosoftIntegration) => {
		await disconnect({ variables: { integration } });
		await refetch();
	};

	return (
		<Container py={4}>
			<Header
				mb={3}
				style={{
					backgroundImage: `url('https://assets.ontreams.com/marketplace/msteams-wide.jpg')`,
					backgroundPosition: 'top center',
				}}
			/>
			<Flex mb={3} alignItems="center">
				<Heading flexGrow={1} flexShrink={1}>
					Microsoft
				</Heading>
			</Flex>
			<Box mb={3}>
				<Paragraph>{t('settings.connectors.microsoft.description')}</Paragraph>
			</Box>

			<PrerequisitesSection />

			{loading || !config ? (
				<>
					<Card.Skeleton height={160} mb={3} />
					<Card.Skeleton height={160} />
				</>
			) : (
				<>
					<IntegrationSection
						title="Microsoft Teams"
						description={t('settings.connectors.microsoft.teams.description')}
						isConnected={config.teamsConnected}
						requiresReconsent={config.teamsRequiresReconsent}
						tenantId={config.tenantId}
						onConnect={() => handleConnect(graphql.MicrosoftIntegration.Teams)}
						onCopyLink={() => requestConsentUrl(graphql.MicrosoftIntegration.Teams)}
						onDisconnect={() => handleDisconnect(graphql.MicrosoftIntegration.Teams)}
					/>
					<IntegrationSection
						title="Microsoft Outlook"
						description={t('settings.connectors.microsoft.outlook.description')}
						isConnected={config.outlookConnected}
						requiresReconsent={config.outlookRequiresReconsent}
						tenantId={config.tenantId}
						onConnect={() => handleConnect(graphql.MicrosoftIntegration.Outlook)}
						onCopyLink={() => requestConsentUrl(graphql.MicrosoftIntegration.Outlook)}
						onDisconnect={() => handleDisconnect(graphql.MicrosoftIntegration.Outlook)}
					/>
				</>
			)}
		</Container>
	);
}

export function MicrosoftTile(props: { selected: boolean }) {
	const { t } = useTranslation();

	return (
		<ProductTile
			id="microsoft"
			title="Microsoft"
			description={t('settings.connectors.microsoft.description')}
			logoImageURL="https://assets.ontreams.com/marketplace/msteams.png"
			selected={props.selected}
			price={0}
		/>
	);
}
